// 事件顯示名稱（SPEC EVT-01：永遠不顯示事件 key；#events-26：正本 = App 畫面，VMS 照抄、不自己維護對照表）。
// 順序：事件目錄（zh-Hant = protocol model/ui.yaml 的 events，抽在 kinio_ui_labels.json；其他語系 = kinio_ui_strings.json 的
// event.<key> / event.<key>.<欄位>.<值>）→ 事件分類（class）的通用名 → 「其他事件」。
// 舊名（目錄之前原生設備與平台用的 motion / person / thing.online / ota.*、本機警報的 pir）先對到目錄的 key，名稱跟新 key 同一個。
// ui.yaml 表達不了、照 App 組字的（#events-26）先算：開關看按幾下、門窗 / 漏水看 state_value 與設備種類（AID TYPE SLK = 漏水）、
// 有人感測看 occupancy、AI 偵測看 payload.class「AI 偵測到人」；字串照抄 app/home-base web 的 kinioEventLabel（events.compose.*）。
import i18n from 'i18next';
import type { TFunction } from 'i18next';
import catalogLabels from './kinio_ui_labels.json';
import { KINIO_MODEL_NS } from './kinioLabel';

type T = TFunction | ((k: string, o?: Record<string, unknown>) => string);
type CatalogEvent = { label: string; labels?: Record<string, Record<string, string>>; alert_class?: string; feed_only?: boolean };

const EVENTS = (catalogLabels as { events: Record<string, CatalogEvent> }).events;

const OBJECT_DETECTED = 'kinio_object_recognition.object_detected';
const SOUND_DETECTED = 'kinio_sound_recognition.sound_detected';

/** 舊名 → 目錄 key（與預設欄位）。 */
const LEGACY: Record<string, { key: string; fields?: Record<string, string> }> = {
  motion: { key: 'kinio_motion.motion' },
  pir: { key: 'kinio_motion.motion', fields: { source: 'pir' } }, // nvrd / stationd 本機警報的舊類型名（protocol mappings/native.yaml）
  person: { key: OBJECT_DETECTED, fields: { object_type: 'person' } },
  human: { key: OBJECT_DETECTED, fields: { object_type: 'person' } },
  'thing.online': { key: 'kinio_system.connected' },
  'thing.offline': { key: 'kinio_system.disconnected' },
  'ota.progress': { key: 'kinio_firmware.ota_progress' },
  'ota.success': { key: 'kinio_firmware.ota_succeeded' },
  'ota.failed': { key: 'kinio_firmware.ota_failed' },
};

/**
 * 警報判斷用的 class（SPEC #events-33 / #events-127，protocol AlertClassOf 的前端對照）：目錄這個 key 有 alert_class 就用它，
 * 否則照傳進來的 class。區域入侵 / 越線的 class 是 alarm（種類、顏色、class 篩選照它），分級照 alert_class motion。
 */
export function alertClassOf(key: string, cls?: string | null): string | undefined {
  return EVENTS[key]?.alert_class ?? cls ?? undefined;
}

/** 目錄標 retention: feed_only 的事件（片段上傳、錄影開始 / 結束、雲台移動…狀態紀錄）：不進全文索引（SPEC #events-88 / #vms-web-496）。 */
export function isFeedOnly(key: string): boolean {
  return !!EVENTS[key]?.feed_only;
}

/** 目錄名稱的當前語系：zh-Hant 用 ui.yaml 原文；其他語系查 kinio_ui_strings，沒有譯文回空字串（不夾中文）。 */
function localized(lng: string, id: string, zh: string): string {
  if (lng === 'zh-Hant') return zh;
  return i18n.t(id, { lng, ns: KINIO_MODEL_NS, keySeparator: false, nsSeparator: false, defaultValue: '' });
}

/** 事件目錄的名稱；目錄沒有這個 key（或這個語系沒有譯文）回空字串。 */
export function catalogEventName(key: string, fields?: Record<string, unknown> | null, lng?: string): string {
  const lang = lng ?? i18n.resolvedLanguage ?? i18n.language ?? 'zh-Hant';
  const legacy = LEGACY[key];
  const k = legacy?.key ?? key;
  const f: Record<string, unknown> = { ...legacy?.fields, ...fields };
  const ev = EVENTS[k];
  const candidates: [string, CatalogEvent | undefined][] = [[k, ev]];
  // 目錄沒有的舊 key 只帶 object_type / sound_class：借物件辨識 / 聲音辨識的細名。目錄裡的 key 照它自己的名稱
  // （越線 / 區域入侵也帶 object_type，名稱仍是「越線偵測」「區域入侵」，ui.yaml 沒有依 object_type 換名）
  if (!ev && typeof f.object_type === 'string') candidates.unshift([OBJECT_DETECTED, EVENTS[OBJECT_DETECTED]]);
  if (!ev && typeof f.sound_class === 'string') candidates.unshift([SOUND_DETECTED, EVENTS[SOUND_DETECTED]]);
  for (const [ck, ce] of candidates) {
    for (const [field, names] of Object.entries(ce?.labels ?? {})) {
      const v = f[field];
      if (typeof v === 'string' && names[v]) {
        const s = localized(lang, `event.${ck}.${field}.${v}`, names[v]);
        if (s) return s;
      }
    }
  }
  return ev ? localized(lang, `event.${k}`, ev.label) : '';
}

function asBool(v: unknown): boolean | null {
  if (typeof v === 'boolean') return v;
  if (v === 0 || v === 1) return v === 1;
  return null;
}

/** AID 的 TYPE（device_ref 第一段，子通道去掉 :chN）；拿不到回空字串。 */
function typeCodeOf(deviceRef?: string | null): string {
  const m = /^([A-Z]{3})-/.exec((deviceRef ?? '').trim().toUpperCase());
  return m ? m[1] : '';
}

/** 照 App 依 payload / 設備種類組的名稱（ui.yaml 的名稱是沒有 payload 時的通用名）；不適用回空字串。 */
function composedEventName(tr: (k: string, o?: Record<string, unknown>) => string, key: string, f: Record<string, unknown>, deviceRef: string | null | undefined, cls: string | null | undefined, lng: string): string {
  switch (key) {
    case 'switch.multi_press_complete': {
      const n = f.total_number_of_presses_counted;
      return typeof n === 'number' && Number.isFinite(n) && n > 0 ? tr('events.compose.multi_press_n', { count: Math.round(n) }) : '';
    }
    case 'boolean_state.state_change': {
      const v = asBool(f.state_value);
      if (v === null) return '';
      // 拿不到設備種類時當門窗（同 App 事件頁）
      if (typeCodeOf(deviceRef) === 'SLK') return tr(v ? 'events.compose.leak_detected' : 'events.compose.leak_cleared');
      return tr(v ? 'events.compose.contact_closed' : 'events.compose.contact_opened');
    }
    case 'occupancy_sensing.occupancy_changed':
      return 'occupancy' in f ? tr(f.occupancy ? 'events.compose.occupied' : 'events.compose.unoccupied') : '';
    case 'kinio_ai_compute.detection': {
      const c = typeof f.class === 'string' && f.class ? f.class : cls;
      const what = c ? catalogEventName(OBJECT_DETECTED, { object_type: c === 'animal' ? 'pet' : c }, lng) : '';
      return what && c && ['person', 'vehicle', 'pet', 'animal', 'package'].includes(c) ? tr('events.compose.ai_detected', { what }) : '';
    }
    default:
      return '';
  }
}

/** 事件目錄的名稱（含照 App 依 payload 組的）；沒有回空字串（呼叫端自己決定退路）。 */
export function knownEventName(t: T, key: string, fields?: Record<string, unknown> | null, deviceRef?: string | null, cls?: string | null): string {
  const lng = (t as { lng?: string }).lng;
  const lang = lng ?? i18n.resolvedLanguage ?? i18n.language ?? 'zh-Hant';
  const tr = (k: string, o?: Record<string, unknown>) => (t as (k: string, o?: Record<string, unknown>) => string)(k, { defaultValue: '', ...o });
  const composed = key ? composedEventName(tr, key, fields ?? {}, deviceRef, cls, lang) : '';
  if (composed) return composed;
  return key || fields ? catalogEventName(key, fields, lng) : '';
}

export function eventName(t: T, key: string, cls?: string | null, fields?: Record<string, unknown> | null, deviceRef?: string | null): string {
  const tr = (k: string) => (t as (k: string, o?: Record<string, unknown>) => string)(k, { defaultValue: '' });
  const known = knownEventName(t, key, fields, deviceRef, cls);
  if (known) return known;
  const byCls = cls ? tr(`events.cls_name.${cls.toLowerCase()}`) : '';
  if (byCls) return byCls;
  return tr('events.type_unknown') || key;
}
